import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import cls from "./MyFriends.module.scss";

import api from "@/utils/axios";
import avatarPng from "@/assets/UI/avatar.png";
import TextPostCard from "@/components/Post/TextPostCard/TextPostCard";
import PhotoPostCard from "@/components/Post/PhotoPostCard/PhotoPostCard";
import VideoPostCard from "@/components/Post/VideoPostCard/VideoPostCard";

const MyFriends = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [friends, setFriends] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [error, setError] = useState("");
  const [friendsOnlyPosts, setFriendsOnlyPosts] = useState([]);
  const [friendsOnlyPostsTotal, setFriendsOnlyPostsTotal] = useState(0);
  const [friendsOnlyPostsLoading, setFriendsOnlyPostsLoading] = useState(false);
  const [friendsOnlyPostsError, setFriendsOnlyPostsError] = useState("");
  const location = useLocation();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";
  const loadFriends = async (nextPage = 1, replace = false) => {
    try {
      setLoading(true);
      setError("");

      const { data } = await api.get("/v1/friends", {
        params: { page: nextPage },
      });

      const list = data?.friends || [];
      setFriends((prev) => (replace ? list : [...prev, ...list]));
      setPage(data?.page || nextPage);
      setHasMore(Boolean(data?.hasMore));
      setInitialLoaded(true);
    } catch (err) {
      console.error("Failed to load friends", err);
      setError(err?.response?.data?.message || t("myFriends.state.error"));
      setInitialLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFriends(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setFriendsOnlyPostsLoading(true);
        setFriendsOnlyPostsError("");

        const { data } = await api.get("/v1/posts/my-friends-feed", {
          params: { page: 1, limit: 15 },
        });

        if (ignore) return;

        const rows =
          data?.rows || data?.posts || data?.data || data?.items || [];
        setFriendsOnlyPosts(Array.isArray(rows) ? rows : []);
        setFriendsOnlyPostsTotal(data?.meta?.total ?? rows.length);
      } catch (err) {
        console.error("Failed to load my friends-only posts", err);
        if (!ignore) {
          setFriendsOnlyPostsError(
            err?.response?.data?.message ||
              t("memberProfileOverview.posts.error"),
          );
        }
      } finally {
        if (!ignore) {
          setFriendsOnlyPostsLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [t]);

  useEffect(() => {
    const loadMe = async () => {
      try {
        const { data } = await api.get("/v1/auth/me");
        setMe(data?.user || data);
      } catch (err) {
        console.error("Failed to load current user", err);
      }
    };

    loadMe();
  }, []);

  const username = me?.username || "User";

  const friendsKnowPersonally = useMemo(
    () => friends.filter((f) => f.connectionType === "know_personally"),
    [friends],
  );

  const friendsOnlineOnly = useMemo(
    () => friends.filter((f) => f.connectionType === "online_only"),
    [friends],
  );

  const friendsOther = useMemo(
    () =>
      friends.filter(
        (f) =>
          f.connectionType !== "know_personally" &&
          f.connectionType !== "online_only",
      ),
    [friends],
  );

  const totalCount = friends.length;

  const renderFeedPost = (post) => {
    const type = String(post?.type || "").toLowerCase();
    const hasVideo = Boolean(post?.videoId || post?.video?.id || post?.video);
    const hasImages =
      (Array.isArray(post?.images) && post.images.length > 0) ||
      (Array.isArray(post?.imageUrls) && post.imageUrls.length > 0);

    if (type === "video-post" || type === "video") {
      return <VideoPostCard post={post} />;
    }
    if (type === "photo-post" || type === "photo") {
      return <PhotoPostCard post={post} />;
    }
    if (type === "text-only" || type === "text") {
      return <TextPostCard post={post} />;
    }

    if (hasVideo) return <VideoPostCard post={post} />;
    if (hasImages) return <PhotoPostCard post={post} />;
    return <TextPostCard post={post} />;
  };

  const handleViewProfile = (friend) => {
    const memberId = friend?.member?._id;
    if (!memberId) return;
    navigate(`/lounge/member-profile/${memberId}`);
  };

  const renderFriendCard = (friend) => {
    const user = friend.user || {};
    const member = friend.member || {};
    const avatarSrc = member.avatar?.url || avatarPng;

    let friendsSinceLabel = t("myFriends.friend.friendsSinceUnknown");
    if (friend.friendsSince) {
      const d = new Date(friend.friendsSince);
      if (!Number.isNaN(d.getTime())) {
        friendsSinceLabel = t("myFriends.friend.friendsSince", {
          date: d.toLocaleDateString(),
        });
      }
    }

    return (
      <article key={friend.id} className={cls.friendCard}>
        <div className={cls.friendCardTop}>
          <div className={cls.friendAvatarWrap}>
            <img
              src={avatarSrc}
              alt={user.username || t("myFriends.friend.unknownUsername")}
              className={cls.friendAvatar}
            />
          </div>

          <div className={cls.friendMain}>
            <div className={cls.friendTopRow}>
              <div className={cls.friendNameBlock}>
                <span className={cls.friendUsername}>
                  {user.username || t("myFriends.friend.unknownUsername")}
                </span>

                <div className={cls.friendMetaInline}>
                  <span className={cls.friendMetaText}>
                    {friendsSinceLabel}
                  </span>
                </div>
              </div>
            </div>

            <div className={cls.friendBadges}>
              <button
                type="button"
                className={cls.friendViewBtn}
                onClick={() => handleViewProfile(friend)}
              >
                {t("myFriends.friend.viewProfile")}
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  };

  const renderGroupSection = (titleKey, emptyKey, list) => (
    <section className={cls.card}>
      <div className={cls.cardHeaderRow}>
        <div>
          <h2 className={cls.cardTitle}>{t(titleKey)}</h2>
          <span className={cls.cardMeta}>
            {t("myFriends.countLabel", { count: list.length })}
          </span>
        </div>
      </div>

      {!loading && initialLoaded && list.length === 0 && (
        <p className={cls.emptyText}>{t(emptyKey)}</p>
      )}

      {list.length > 0 && (
        <div className={cls.friendsGrid}>
          {list.map((f) => renderFriendCard(f))}
        </div>
      )}
    </section>
  );

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>

          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>

          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>

          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>

          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <section className={cls.header}>
          <div className={cls.headerMain}>
            <div className={cls.headerInfo}>
              <h1 className={cls.displayName}>{t("myFriends.title")}</h1>

              <div className={cls.usernameRow}>
                <span className={cls.username}>@{username}</span>
              </div>

              <div className={cls.metaRow}>
                <span className={`${cls.metaPill} ${cls.metaPillType}`}>
                  {totalCount} Friends
                </span>

                <span className={`${cls.metaPill} ${cls.metaPillLocation}`}>
                  {friendsKnowPersonally.length} Know Personally
                </span>

                <span className={`${cls.metaPill} ${cls.metaPillAge}`}>
                  {friendsOnlineOnly.length} Online Only
                </span>
              </div>
            </div>
          </div>

          <div className={cls.headerActions}>
            <button
              type="button"
              className={cls.btnGhost}
              onClick={() => loadFriends(1, true)}
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </section>

        <nav className={cls.tabs}>
          <Link to="/lounge/my-profile" className={cls.tabButton}>
            {t("memberProfileOverview.tabs.timeline")}
          </Link>

          <Link to="/lounge/my-profile/about" className={cls.tabButton}>
            {t("memberProfileOverview.tabs.about")}
          </Link>

          <Link
            to="/lounge/friends"
            className={`${cls.tabButton} ${cls.activeTab}`}
          >
            {t("memberProfileOverview.tabs.friends")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.leftColumn}>
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <div>
                  <h2 className={cls.cardTitle}>{t("myFriends.title")}</h2>
                  <span className={cls.cardMeta}>
                    {t("myFriends.countLabel", { count: totalCount })}
                  </span>
                </div>
              </div>

              {!initialLoaded && loading && (
                <div className={cls.stateRow}>
                  <span className={cls.stateDot} />
                  <span className={cls.stateText}>
                    {t("myFriends.state.loading")}
                  </span>
                </div>
              )}

              {initialLoaded && !loading && error && (
                <div className={cls.stateRowError}>
                  <span className={cls.stateDot} />
                  <span className={cls.stateText}>{error}</span>
                </div>
              )}

              {initialLoaded && !loading && !error && totalCount === 0 && (
                <p className={cls.emptyText}>
                  {t("myFriends.state.noFriends")}
                </p>
              )}

              {initialLoaded && !error && totalCount > 0 && (
                <div className={cls.quickStats}>
                  <div className={cls.statItem}>
                    <span className={cls.statNumber}>{totalCount}</span>
                    <span className={cls.statLabel}>
                      {t("myFriends.title")}
                    </span>
                  </div>

                  <div className={cls.statItem}>
                    <span className={cls.statNumber}>
                      {friendsKnowPersonally.length}
                    </span>
                    <span className={cls.statLabel}>
                      {t("myFriends.groups.knowPersonallyTitle")}
                    </span>
                  </div>

                  <div className={cls.statItem}>
                    <span className={cls.statNumber}>
                      {friendsOnlineOnly.length}
                    </span>
                    <span className={cls.statLabel}>
                      {t("myFriends.groups.onlineOnlyTitle")}
                    </span>
                  </div>
                </div>
              )}
            </section>

            {friendsKnowPersonally.length > 0 || initialLoaded
              ? renderGroupSection(
                  "myFriends.groups.knowPersonallyTitle",
                  "myFriends.groups.knowPersonallyEmpty",
                  friendsKnowPersonally,
                )
              : null}

            {friendsOnlineOnly.length > 0 || initialLoaded
              ? renderGroupSection(
                  "myFriends.groups.onlineOnlyTitle",
                  "myFriends.groups.onlineOnlyEmpty",
                  friendsOnlineOnly,
                )
              : null}

            {friendsOther.length > 0 &&
              renderGroupSection(
                "myFriends.groups.otherTitle",
                "myFriends.groups.otherEmpty",
                friendsOther,
              )}

            {hasMore && (
              <div className={cls.loadMoreRow}>
                <button
                  type="button"
                  className={cls.btnPrimary}
                  onClick={() => loadFriends(page + 1)}
                  disabled={loading}
                >
                  {loading
                    ? t("myFriends.state.loading")
                    : t("myFriends.state.loadMore")}
                </button>
              </div>
            )}
          </div>

          <div className={cls.rightColumn}>
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <div>
                  <h2 className={cls.cardTitle}>
                    {t("memberProfileOverview.postsTitle")}
                  </h2>
                  <span className={cls.cardMeta}>
                    {t("memberProfileOverview.postsCountFriends", {
                      count: friendsOnlyPostsTotal,
                    })}
                  </span>
                </div>
              </div>

              {friendsOnlyPostsLoading && (
                <div className={cls.stateRow}>
                  <span className={cls.stateDot} />
                  <span className={cls.stateText}>
                    {t("memberProfileOverview.posts.loading")}
                  </span>
                </div>
              )}

              {!friendsOnlyPostsLoading && friendsOnlyPostsError && (
                <div className={cls.stateRowError}>
                  <span className={cls.stateDot} />
                  <span className={cls.stateText}>
                    {friendsOnlyPostsError ||
                      t("memberProfileOverview.posts.error")}
                  </span>
                </div>
              )}

              {!friendsOnlyPostsLoading &&
                !friendsOnlyPostsError &&
                friendsOnlyPostsTotal === 0 && (
                  <p className={cls.emptyText}>
                    {t("memberProfileOverview.posts.empty")}
                  </p>
                )}

              {!friendsOnlyPostsLoading &&
                !friendsOnlyPostsError &&
                friendsOnlyPosts.length > 0 && (
                  <div className={cls.postList}>
                    {friendsOnlyPosts.map((post) => (
                      <div key={post._id} className={cls.postCardWrap}>
                        {renderFeedPost(post)}
                      </div>
                    ))}
                  </div>
                )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyFriends;
